import Divider from "components/divider";
import Page from "components/page/Page";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./eventsCalendarPage.scss";
import { monthNames, openPrintWindow } from "helpers/Helpers";
import SelectNew from "components/select/SelectNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import EventsCalendar from "./eventsCalendar/EventsCalendar";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { StatusCode } from "api/protocols";
import { GetEventCalendarPrint } from "api/rpc/2024-04/facilityAdmin/event/hospitalityEvent";

interface IState {
    months: Array<{month: string; id: number}>;
    years: Array<{year: number; id: number}>;
}

interface IDateState {
    month: number;
    year: number;
    date: Date;
}

type TEventFilter = "all_events" | "golf" | "hospitality"

export default function EventsCalendarPage() {
    const { t }= useTranslation();
    const dispatch = useAppDispatch();
    const [newEventDrawerOpen, setNewEventDrawerOpen] = useState(false);
    const [state, setState] = useState<IState>({
        months: [],
        years: [],
    });

    //Date selection state
    const [dateState, setDateState] = useState<IDateState>({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        date: new Date(),
    });

    const [filterState, setFilterState] = useState<TEventFilter>("all_events");

    useEffect(() => {
        const months: { month: any; id: number }[] = [];
        const years: { year: number; id: number }[] = [];
    
        monthNames(t).map((monthName: any, index: number) => {
          const month = {
            month: monthName,
            id: index + 1,
          };
    
          months.push(month);
        });
    
        let startingYear = new Date().getFullYear() - 5;
        const endingYear = new Date().getFullYear() + 5;
    
        for (let i = startingYear; i <= endingYear; i++) {
          const year = {
            year: startingYear,
            id: startingYear,
          };
    
          years.push(year);
          startingYear++;
        }
    
        setState(prevState => ({ ...prevState, months: months, years: years }));
      }, []);


      function handleMonthChange(value: number) {
        const newDate = new Date(dateState?.year, value - 1, new Date()?.getDate());
        newDate.setMonth(newDate.getMonth());
        setDateState(prev => ({...prev, month: value, date: newDate}));
      }
    
      function handleYearChange(value: number) {
        const newDate = new Date(value, dateState.month - 1, new Date()?.getDate());
        newDate.setMonth(newDate.getMonth());
        setDateState(prev => ({...prev, year: value, date: newDate}));
      }

      function shiftMonth(monthsToShiftBy: number) {
        const newDate = new Date(dateState.date.getFullYear(), dateState.date.getMonth(), new Date()?.getDate());
        newDate.setMonth(newDate.getMonth() + monthsToShiftBy);
        setDateState(prev => ({...prev, date: newDate, month: newDate.getMonth() + 1, year: newDate.getFullYear()}));
      }

    async function handlePrintCalendar() {
      const res = await GetEventCalendarPrint({
        year: dateState.year,
        month: dateState.month,
        filter: filterState,
      }, true);

      if (res.status !== StatusCode.OK) {
        dispatch(showError(t("Error printing events calendar")));
        return;
      }

      openPrintWindow(String(res.data));
    }

    return (
        <Page
        title={t("Events calendar")}
        subtitle={t("View and manage all facility events here.")}
        primaryAction={{content: t("New event"), action: () => setNewEventDrawerOpen(true)}}
        multipleActionDropdownAction={{
          label: t("Options"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("Print"),
                icon: "print",
                handler: () => {
                  void handlePrintCalendar();
                },
              },
            ],
          },
        }}
        >
        <Divider/>
        <div className="ui-events-calendar-page-date-container">
            <div className="flex items-center gap-4">
            <button className="navigation-button" onClick={() => shiftMonth(-1)}>
                  <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="flex items-center gap-4" style={{minWidth: "300px"}}>
            <div className="w-100">
            <SelectNew
                id="month_select"
                options={state?.months?.map(month => ({label: month.month, id: month.id}))}
                value={dateState?.month}
                onChange={value => handleMonthChange(value)}
            />
            </div>

            <div className="w-100">
            <SelectNew
                id="year_select"
                options={state?.years?.map(year => ({label: year.year, id: year.id}))}
                value={dateState?.year}
                onChange={value => handleYearChange(value)}
            />
            </div>
            </div>
            <button className="navigation-button" onClick={() => shiftMonth(1)}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            <SelectNew
                id="filter_select"
                options={[{label: t("All events"), id: "all_events"}, {label: t("Golf"), id: "golf"}, {label: t("Hospitality"), id: "hospitality"}]}
                value={filterState}
                onChange={value => setFilterState(value)}
            />
        </div>
        <br />
        <EventsCalendar 
          currentDate={dateState?.date} 
          filter={filterState} 
          newEventDrawerOpen={newEventDrawerOpen}
          closeNewEventDrawer={() => setNewEventDrawerOpen(false)}
          openNewEventDrawer={() => setNewEventDrawerOpen(true)}
          />
        </Page>
    );
}
